import React, { useCallback, useEffect, useMemo, useState } from "react";

import { Stack } from "@mui/material";
import { format, set } from "date-fns";
import { useFlags } from "launchdarkly-react-client-sdk";
import { FormProvider, useForm } from "react-hook-form";
import { useLocation, useNavigate } from "react-router-dom-v5-compat";

import useGetPortfolioBillingCsv from "@fartherfinance/frontend/api/Investment/hooks/useGetPortfolioBillingCsv";
import useGetPortfolioBillingVersions from "@fartherfinance/frontend/api/Investment/hooks/useGetPortfolioBillingVersions";
import { PortfolioBillingVersionId } from "@fartherfinance/frontend/api/Investment/types";

import { downloadToFile } from "@src/multiCustodian/components/Admin/utils/downloadToFile";
import Drawer from "@src/multiCustodian/components/Drawer/Drawer";
import ButtonPrimary from "@src/multiCustodian/components/MUI/Button/Button";
import useAdvisorRequestAuth from "@src/multiCustodian/hooks/useAdvisorRequestAuth";
import useStatusNotification from "@src/multiCustodian/hooks/useStatusNotification";
import FlexWrapper from "@src/sharedComponents/Forms/FlexWrapper";
import FormDropdownField from "@src/sharedComponents/Forms/FormDropdownField";
import LogoLoadingStill from "@src/sharedComponents/LogoLoadingStill/LogoLoadingStill";
import Text from "@src/sharedComponents/Typography/Text";
import { handleApiError } from "@src/utils/handleApiError";

import { strings } from "./strings";

const MonthYear = "MonthYear";
const Day = "Day";

type ReportType = "fam" | "non-fam";

interface Form {
  [MonthYear]: { label: string; value: PortfolioBillingVersionId } | undefined;
  [Day]: { label: string; value: PortfolioBillingVersionId } | undefined;
}

const AllPortfoliosBilling = (): JSX.Element => {
  const navigate = useNavigate();
  const location = useLocation();

  const [portfolioBillingVersionId, setPortfolioVersionBillingId] = useState<
    PortfolioBillingVersionId | undefined
  >(undefined);

  // Extract reportType from URL query parameters
  const reportType = useMemo((): ReportType | undefined => {
    const searchParams = new URLSearchParams(location.search);
    const rawReportType = searchParams.get("reportType");

    // Validate that the reportType is one of the allowed values
    if (rawReportType === "fam" || rawReportType === "non-fam") {
      return rawReportType;
    }

    return undefined;
  }, [location.search]);

  const methods = useForm<Form>({
    mode: "all",
    reValidateMode: "onChange",
    defaultValues: {
      [MonthYear]: undefined,
      [Day]: undefined,
    },
  });

  const statusNotification = useStatusNotification();

  const { enableAdminFamBilling } = useFlags();

  const auth = useAdvisorRequestAuth();

  const { data: billingData, isLoading: isLoadingBillingVersions } =
    useGetPortfolioBillingVersions(
      {
        page: 1,
        pageSize: 100,
      },
      auth
    );

  const {
    handleSubmit,
    watch,
    setValue,
    formState: { isValid, isDirty },
  } = methods;

  const selectedMonthYear = watch(MonthYear);

  // Get the selected month and year for the billingDataPerDay query
  const selectedMonthYearData = useMemo(() => {
    if (!selectedMonthYear || !billingData?.data) {
      return null;
    }

    const selectedBilling = billingData.data.find(
      (item) => item.portfolioBillingVersionId === selectedMonthYear.value
    );

    return selectedBilling
      ? {
          month: selectedBilling.billingMonth,
          year: selectedBilling.billingYear,
        }
      : null;
  }, [selectedMonthYear, billingData]);

  const { data: billingDataPerDay, isLoading: isLoadingBillingVersionsPerDay } =
    useGetPortfolioBillingVersions(
      selectedMonthYearData
        ? {
            page: 1,
            pageSize: 100,
            month: selectedMonthYearData.month,
            year: selectedMonthYearData.year,
          }
        : null,
      selectedMonthYearData ? auth : null
    );

  const { data: custodianData, isLoading: isLoadingCustodian } =
    useGetPortfolioBillingCsv(
      portfolioBillingVersionId
        ? {
            versionId: portfolioBillingVersionId,
            reportType,
          }
        : null,
      portfolioBillingVersionId ? auth : null
    );

  const isLoading =
    isLoadingBillingVersions ||
    (isLoadingCustodian && portfolioBillingVersionId !== undefined);

  const billingPerDayLoading = Boolean(
    isLoadingBillingVersionsPerDay && selectedMonthYearData
  );

  const monthYearOptions = useMemo(() => {
    if (billingData?.data === undefined) {
      return [];
    }

    return billingData?.data.map((item) => {
      const date = set(new Date(), {
        year: item.billingYear,
        month: item.billingMonth - 1,
      });

      return {
        label: format(date, "MMMM yyyy"),
        value: item.portfolioBillingVersionId,
      };
    });
  }, [billingData]);

  const dayOptions = useMemo(() => {
    if (billingDataPerDay?.data === undefined) {
      return [];
    }

    return billingDataPerDay?.data.map((item) => {
      const date = set(new Date(), {
        year: item.billingYear,
        month: item.billingMonth - 1,
        date: item.billingDay,
      });

      return {
        label: format(date, "MMMM d, yyyy"),
        value: item.portfolioBillingVersionId,
      };
    });
  }, [billingDataPerDay]);

  useEffect(() => {
    if (!enableAdminFamBilling) {
      navigate({ ...location, pathname: "/Advisor/Clients" });
    }
  }, [enableAdminFamBilling, location, navigate]);

  // Clear day selection when month changes
  useEffect(() => {
    setValue(Day, undefined);
  }, [selectedMonthYear, setValue]);

  const onClose = useCallback(
    () =>
      navigate({
        ...location,
        pathname: "/Advisor/Admin",
        search: "",
      }),
    [location, navigate]
  );

  const submit = handleSubmit(async (values: Form) => {
    if (values[MonthYear] === undefined) {
      statusNotification(strings.SELECT_BILLING_PERIOD, "Error");
      return;
    }

    // Use the specific day if selected, otherwise use the month
    const formPortfolioBillingId =
      values[Day]?.value || values[MonthYear].value;

    setPortfolioVersionBillingId(formPortfolioBillingId);
  });

  useEffect(() => {
    if (isLoading) {
      return;
    }

    if (portfolioBillingVersionId === undefined) {
      return;
    }

    try {
      if (custodianData !== undefined && custodianData?.length > 0) {
        downloadToFile(custodianData, "allPortfolios", "text/csv");
        statusNotification(strings.DOWNLOAD_SUCCESS, "Success");
        onClose();
      } else {
        statusNotification(strings.NO_BILLING_DATA, "Error");
        setPortfolioVersionBillingId(undefined);
      }
    } catch (error) {
      setPortfolioVersionBillingId(undefined);
      handleApiError({
        error,
        errorMessage: strings.DOWNLOAD_FAILURE,
        genericErrorMessage: strings.DOWNLOAD_FAILURE,
        notify: statusNotification,
      });
    }
  }, [
    portfolioBillingVersionId,
    statusNotification,
    custodianData,
    onClose,
    isLoading,
  ]);

  useEffect(() => {
    return () => {
      setPortfolioVersionBillingId(undefined);
      methods.reset();
    };
  }, [methods]);

  return (
    <Drawer
      isDrawerOpen
      onClose={onClose}
      footer={
        <Stack direction="row" gap="16px">
          <ButtonPrimary
            text={strings.CANCEL_BUTTON}
            variant="outlined"
            buttonType="primary"
            onClick={onClose}
          />

          <ButtonPrimary
            disabled={!isDirty || !isValid || isLoading || billingPerDayLoading}
            text={strings.DOWNLOAD_CSV_BUTTON}
            variant="contained"
            buttonType="primary"
            onClick={submit}
          />
        </Stack>
      }
    >
      {isLoading && <LogoLoadingStill onTop />}

      <FormProvider {...methods}>
        <Text variant="title3">
          {reportType === "fam"
            ? strings.TITLE_FAM
            : reportType === "non-fam"
            ? strings.TITLE_NON_FAM
            : strings.TITLE}
        </Text>

        <Text variant="body" color="textSubtle">
          {reportType === "fam"
            ? strings.DESCRIPTION_FAM
            : reportType === "non-fam"
            ? strings.DESCRIPTION_NON_FAM
            : strings.DESCRIPTION}
        </Text>

        <FlexWrapper>
          <FormDropdownField
            name={MonthYear}
            label={strings.MONTH_LABEL}
            values={monthYearOptions}
            disableSearch
            required={strings.MONTH_REQUIRED}
            placeholder={strings.MONTH_PLACEHOLDER}
          />
          <FormDropdownField
            name={Day}
            label={strings.DAY_LABEL}
            values={dayOptions}
            disableSearch
            placeholder={strings.DAY_PLACEHOLDER}
            disabled={billingPerDayLoading}
          />
        </FlexWrapper>
      </FormProvider>
    </Drawer>
  );
};

export default AllPortfoliosBilling;
